import { determineSignAlgorithm } from '@/sign/helpers/sign.helper';
import { AppSignKeyOperationType, SignMethod, SignPurpose } from '@/sign/models/sign-constants';
import { SignPrivateKeyStrategy } from './sign-private-key.strategy';
import { documentReceiptConfirmsService } from '@/services/document-receipt-confirms.service';
import { SigningParams } from '@/sign/models/signing-params.model';
import { MixWithOwnCertFromSigningParams } from '../mixins/deposit-sign.mixin';
import { MixWithDiiaSigning } from '../mixins/diia-sign.mixin';
import { depositSignService } from '@/services/deposit-sign.service';
import { disableShowMsg, enableShowMsg, setLoadingCancelCallback, setLoadingText } from '@/mixins/ui-mixin';
import { cloudKeySignService } from '@/services/cloud-key-sign.service';
import { MixWithCloudKeyFileSystemKeyType } from '../mixins/cloud-key-sign.mixin';
import { EventBus } from '@/helpers/event-bus';
import { executeCloudSignOperationWithConfirmationByQrCode } from '../helpers/cloud-sign-qr.helper';
import { getLocale } from '@/helpers';

abstract class ConfirmDocumentReceiptPrivateKeyStrategy extends SignPrivateKeyStrategy {
  private documentReceiptConfirmUid: string;

  constructor({ documentReceiptConfirmUid }) {
    super();
    this.documentReceiptConfirmUid = documentReceiptConfirmUid;
    this.signPurpose = SignPurpose.ConfirmDocumentReceipt;
  }

  public async execute(prms: SigningParams): Promise<any> {
    disableShowMsg();

    try {
      await super.execute(prms);

      await this.init();

      await this.validate();

      await this.determineKeyTypes();

      const { hashBase64, quittanceFileName } = await documentReceiptConfirmsService.getQuittanceHash(
        this.documentReceiptConfirmUid, this.keyType);

      const signAlgo = determineSignAlgorithm(this.keyType);

      const signedHash = await this.signHash(signAlgo, hashBase64, true, true, quittanceFileName);

      await documentReceiptConfirmsService.confirm(this.documentReceiptConfirmUid, signedHash, this.keyType);

      return true;
    } finally {
      enableShowMsg();
    }
  }

  protected async signHash(signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean, fileName?: string): Promise<any> {
    return this.euSignLib.CtxSignHash(this.privateKeyContext, signAlgo, hash, appendCert, asBase64String);
  }
}

export class ConfirmDocumentReceiptFileSystemStrategy extends ConfirmDocumentReceiptPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.FileSystem;
}

export class ConfirmDocumentReceiptDeviceStrategy extends ConfirmDocumentReceiptPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.Device;
}

export class ConfirmDocumentReceiptCloudStrategy extends ConfirmDocumentReceiptPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.Cloud;

  constructor(prms: any) {
    super(prms);

    this.mobileAppName = prms?.mobileAppName;
  }
}

export class ConfirmDocumentReceiptDepositSignLoginPswStrategy
  extends MixWithOwnCertFromSigningParams(ConfirmDocumentReceiptCloudStrategy) {
  protected async signHash(signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean, fileName?: string): Promise<any> {
    let { depositSignUserSessionId, keyId, keyPassword } = this.signingParams.data;

    const signes = await depositSignService.signHashes(depositSignUserSessionId, keyId, keyPassword, [{ id: '1', data: hash }]);

    return signes && signes.length ? signes[0].sign : null;
  }
}

export class ConfirmDocumentReceiptDiiaStrategy
  extends MixWithDiiaSigning(ConfirmDocumentReceiptCloudStrategy) {
  protected async signHash(signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean, fileName?: string): Promise<any> {
    const hashes = [{ fileName: fileName, fileHash: hash }];

    let signedHashes = await this.signFileHashesWithDiia(hashes);

    return signedHashes[0].signature;
  }
}

export class ConfirmDocumentReceiptCloudKeyFileSystemStrategy
  extends MixWithCloudKeyFileSystemKeyType(ConfirmDocumentReceiptCloudStrategy) {
  protected async signHash(signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean, fileName?: string): Promise<any> {
    const { certificate, privateKeyPassword } = this.signingParams.data;

    const signs = await cloudKeySignService.signHashes(certificate, privateKeyPassword, [hash]);

    return signs && signs.length ? signs[0] : null;
  }
}

export class ConfirmDocumentReceiptWithQrCloudProviderStrategy
  extends ConfirmDocumentReceiptCloudStrategy {
  protected getOwnCertificates(): Promise<any> {
    return this.euSignLib.GetOwnCertificates();
  }

  protected async signHash(
    signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean, fileName?: string): Promise<any> {
    try {
      return await executeCloudSignOperationWithConfirmationByQrCode(
        this.signingParams.signProvider,
        this.euSignLib,
        'SignHash',
        [signAlgo, hash, appendCert, asBase64String],
        this.signingParams.privateKeyType,
        AppSignKeyOperationType.Signing,
        this.mobileAppName
      );
    } catch (error) {
      if (/cancelled/i.test(error as any)) {
        EventBus.$emit('app-sign-reset-private-key', {
          privateKeyType: this.signingParams?.privateKeyType
        });
      }
      throw error;
    }
  }
}

export class ConfirmDocumentReceiptWithIdCloudProviderStrategy
  extends ConfirmDocumentReceiptCloudStrategy {
  protected getOwnCertificates(): Promise<any> {
    return this.euSignLib.GetOwnCertificates();
  }

  protected async signHash(
    signAlgo: number, hash: string, appendCert: boolean, asBase64String?: boolean, fileName?: string): Promise<any> {
    setLoadingCancelCallback(() => this.euSignLib.ResetOperationKSP());

    setLoadingText({
      text: getLocale('appSignCloudProviderDefault.selectKeyInMobileApp', { mobileAppName: this.mobileAppName }).toString()
    });

    try {
      return await this.euSignLib.SignHash(signAlgo, hash, appendCert, asBase64String);
    } finally {
      setLoadingCancelCallback(null);

      setLoadingText({ text: '' });
    }
  }
}
